
import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { monitoringSystem } from '@/core/MonitoringSystem';
import { debugLogger } from '@/utils/debugLogger';

interface DatadogMetrics {
  timestamp: string;
  collection_duration_ms: number;
  environment: string;
  supabase_metrics: {
    database: {
      active_connections: number;
      query_performance_avg_ms: number;
      connection_pool_utilization: number;
      storage_used_mb: number;
    };
    auth: {
      active_sessions: number;
      login_attempts_last_minute: number;
      failed_logins_last_minute: number;
      token_refresh_rate: number;
    };
    realtime: {
      websocket_connections: number;
      messages_per_second: number;
      subscription_count: number;
      latency_avg_ms: number;
    };
  };
  business_metrics: {
    users: {
      total_users: number;
      active_users_last_hour: number;
      users_online_now: number;
      new_registrations_today: number;
      verification_completion_rate: number;
    };
    engagement: {
      messages_sent_last_minute: number;
      conversations_active_now: number;
      connections_created_last_minute: number;
      match_rate_percentage: number;
      profile_views_last_minute: number;
    };
    content: {
      photos_uploaded_today: number;
      successful_uploads_rate: number;
      verification_requests_pending: number;
      feedbacks_submitted_today: number;
    };
  };
  frontend_metrics: {
    performance: {
      page_load_time_avg: number;
      javascript_errors_count: number;
      memory_usage_mb: number;
      cache_hit_rate: number;
    };
    user_experience: {
      bounce_rate: number;
      session_duration_avg: number;
      feature_usage_rates: Record<string, number>;
      error_rate_percentage: number;
    };
  };
  system_metrics: {
    monitoring: {
      alerts_triggered_last_minute: number;
      health_check_status: string;
      uptime_percentage: number;
      response_time_avg_ms: number;
    };
    security: {
      suspicious_activities_detected: number;
      rate_limit_violations: number;
      failed_authentication_attempts: number;
    };
  };
  health_status: 'healthy' | 'warning' | 'critical';
  alerts: Array<{
    severity: 'low' | 'medium' | 'high' | 'critical';
    message: string;
    timestamp: string;
  }>;
}

export const useDatadogMetrics = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const collectDatabaseMetrics = async () => {
    try {
      // Active connections estimate
      const { data: connectionData } = await supabase
        .from('profiles')
        .select('is_online')
        .eq('is_online', true);

      // Storage metrics
      const { data: storageData } = await supabase.storage
        .from('profile_photos')
        .list('', { limit: 1 });

      return {
        active_connections: connectionData?.length || 0,
        query_performance_avg_ms: 150, // Placeholder - would need custom monitoring
        connection_pool_utilization: 0.3, // Placeholder
        storage_used_mb: 0 // Placeholder - would need storage API
      };
    } catch (error) {
      debugLogger.error('[DatadogMetrics] Error collecting database metrics:', error);
      return {
        active_connections: 0,
        query_performance_avg_ms: 0,
        connection_pool_utilization: 0,
        storage_used_mb: 0
      };
    }
  };

  const collectAuthMetrics = async () => {
    try {
      const oneMinuteAgo = new Date(Date.now() - 60000).toISOString();
      const oneHourAgo = new Date(Date.now() - 3600000).toISOString();

      const { data: recentLogins } = await supabase
        .from('profiles')
        .select('last_login')
        .gte('last_login', oneMinuteAgo);

      const { data: activeSessions } = await supabase
        .from('profiles')
        .select('is_online')
        .eq('is_online', true);

      return {
        active_sessions: activeSessions?.length || 0,
        login_attempts_last_minute: recentLogins?.length || 0,
        failed_logins_last_minute: 0, // Would need auth logs
        token_refresh_rate: 0.95 // Placeholder
      };
    } catch (error) {
      debugLogger.error('[DatadogMetrics] Error collecting auth metrics:', error);
      return {
        active_sessions: 0,
        login_attempts_last_minute: 0,
        failed_logins_last_minute: 0,
        token_refresh_rate: 0
      };
    }
  };

  const collectRealtimeMetrics = async () => {
    const healthReport = monitoringSystem.getHealthReport();
    
    return {
      websocket_connections: healthReport.websocketConnections,
      messages_per_second: healthReport.averageMessageLatency > 0 ? 60 / healthReport.averageMessageLatency : 0,
      subscription_count: healthReport.websocketConnections * 3, // Estimate
      latency_avg_ms: healthReport.averageMessageLatency
    };
  };

  const collectBusinessMetrics = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const oneHourAgo = new Date(Date.now() - 3600000).toISOString();
      const oneMinuteAgo = new Date(Date.now() - 60000).toISOString();

      // Users metrics
      const { data: allUsers } = await supabase
        .from('profiles')
        .select('id, is_online, created_at, last_seen, verification_level, onboarding_completed');

      const { data: recentMessages } = await supabase
        .from('messages')
        .select('id, created_at')
        .gte('created_at', oneMinuteAgo);

      const { data: activeConversations } = await supabase
        .from('conversations')
        .select('id, last_message_time')
        .gte('last_message_time', oneHourAgo);

      const { data: recentConnections } = await supabase
        .from('connections')
        .select('id, created_at, status')
        .gte('created_at', oneMinuteAgo);

      const { data: recentViews } = await supabase
        .from('profile_views')
        .select('id, created_at')
        .gte('created_at', oneMinuteAgo);

      const { data: pendingVerifications } = await supabase
        .from('document_verifications')
        .select('id')
        .eq('status', 'waiting');

      const { data: todayFeedbacks } = await supabase
        .from('feedbacks')
        .select('id')
        .gte('created_at', today);

      const totalUsers = allUsers?.length || 0;
      const activeUsersLastHour = allUsers?.filter(u => 
        u.last_seen && new Date(u.last_seen) > new Date(oneHourAgo)
      ).length || 0;
      const usersOnlineNow = allUsers?.filter(u => u.is_online).length || 0;
      const newRegistrationsToday = allUsers?.filter(u => 
        u.created_at && u.created_at.startsWith(today)
      ).length || 0;
      const verificationCompletionRate = totalUsers > 0 ? 
        (allUsers?.filter(u => u.verification_level === 'full').length || 0) / totalUsers : 0;

      const matchConnections = recentConnections?.filter(c => c.status === 'matched').length || 0;
      const totalConnections = recentConnections?.length || 0;
      const matchRate = totalConnections > 0 ? (matchConnections / totalConnections) * 100 : 0;

      return {
        users: {
          total_users: totalUsers,
          active_users_last_hour: activeUsersLastHour,
          users_online_now: usersOnlineNow,
          new_registrations_today: newRegistrationsToday,
          verification_completion_rate: Math.round(verificationCompletionRate * 100) / 100
        },
        engagement: {
          messages_sent_last_minute: recentMessages?.length || 0,
          conversations_active_now: activeConversations?.length || 0,
          connections_created_last_minute: totalConnections,
          match_rate_percentage: Math.round(matchRate * 100) / 100,
          profile_views_last_minute: recentViews?.length || 0
        },
        content: {
          photos_uploaded_today: 0, // Would need storage logs
          successful_uploads_rate: 0.95, // Placeholder
          verification_requests_pending: pendingVerifications?.length || 0,
          feedbacks_submitted_today: todayFeedbacks?.length || 0
        }
      };
    } catch (error) {
      debugLogger.error('[DatadogMetrics] Error collecting business metrics:', error);
      return {
        users: {
          total_users: 0,
          active_users_last_hour: 0,
          users_online_now: 0,
          new_registrations_today: 0,
          verification_completion_rate: 0
        },
        engagement: {
          messages_sent_last_minute: 0,
          conversations_active_now: 0,
          connections_created_last_minute: 0,
          match_rate_percentage: 0,
          profile_views_last_minute: 0
        },
        content: {
          photos_uploaded_today: 0,
          successful_uploads_rate: 0,
          verification_requests_pending: 0,
          feedbacks_submitted_today: 0
        }
      };
    }
  };

  const collectFrontendMetrics = () => {
    const healthReport = monitoringSystem.getHealthReport();
    
    // Performance metrics from browser APIs
    const memoryInfo = (performance as any).memory;
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    
    return {
      performance: {
        page_load_time_avg: navigation ? navigation.loadEventEnd - navigation.fetchStart : 0,
        javascript_errors_count: healthReport.recentErrors.length,
        memory_usage_mb: memoryInfo ? Math.round(memoryInfo.usedJSHeapSize / 1024 / 1024) : 0,
        cache_hit_rate: healthReport.cacheHitRate
      },
      user_experience: {
        bounce_rate: 0.25, // Placeholder - would need analytics
        session_duration_avg: 900, // 15 minutes placeholder
        feature_usage_rates: {
          messaging: 0.8,
          profile_viewing: 0.95,
          connections: 0.6,
          photo_upload: 0.4
        },
        error_rate_percentage: healthReport.errorRate > 0 ? (healthReport.errorRate / 100) : 0
      }
    };
  };

  const collectSystemMetrics = () => {
    const healthReport = monitoringSystem.getHealthReport();
    const criticalErrors = healthReport.recentErrors.filter(e => e.severity === 'critical');
    
    return {
      monitoring: {
        alerts_triggered_last_minute: criticalErrors.length,
        health_check_status: healthReport.errorRate < 5 ? 'healthy' : 'warning',
        uptime_percentage: 99.9, // Placeholder - would need uptime monitoring
        response_time_avg_ms: healthReport.averageMessageLatency
      },
      security: {
        suspicious_activities_detected: 0, // Would need security monitoring
        rate_limit_violations: 0, // Would need rate limiting logs
        failed_authentication_attempts: 0 // Would need auth failure logs
      }
    };
  };

  const collectMetrics = useCallback(async (): Promise<DatadogMetrics> => {
    const startTime = Date.now();
    setLoading(true);
    setError(null);

    try {
      debugLogger.debug('[DatadogMetrics] Starting metrics collection');

      const [
        databaseMetrics,
        authMetrics,
        realtimeMetrics,
        businessMetrics,
        frontendMetrics,
        systemMetrics
      ] = await Promise.all([
        collectDatabaseMetrics(),
        collectAuthMetrics(),
        collectRealtimeMetrics(),
        collectBusinessMetrics(),
        collectFrontendMetrics(),
        collectSystemMetrics()
      ]);

      const collectionDuration = Date.now() - startTime;
      const healthReport = monitoringSystem.getHealthReport();

      // Determine overall health status
      let healthStatus: 'healthy' | 'warning' | 'critical' = 'healthy';
      if (healthReport.errorRate > 10) {
        healthStatus = 'critical';
      } else if (healthReport.errorRate > 5 || systemMetrics.monitoring.alerts_triggered_last_minute > 0) {
        healthStatus = 'warning';
      }

      // Generate alerts from recent errors
      const alerts = healthReport.recentErrors.slice(0, 5).map(error => ({
        severity: error.severity,
        message: error.message,
        timestamp: new Date(error.timestamp).toISOString()
      }));

      const metrics: DatadogMetrics = {
        timestamp: new Date().toISOString(),
        collection_duration_ms: collectionDuration,
        environment: import.meta.env.MODE || 'development',
        supabase_metrics: {
          database: databaseMetrics,
          auth: authMetrics,
          realtime: realtimeMetrics
        },
        business_metrics: businessMetrics,
        frontend_metrics: frontendMetrics,
        system_metrics: systemMetrics,
        health_status: healthStatus,
        alerts
      };

      debugLogger.debug('[DatadogMetrics] Metrics collection completed:', {
        duration: collectionDuration,
        healthStatus,
        alertsCount: alerts.length
      });

      return metrics;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      debugLogger.error('[DatadogMetrics] Error collecting metrics:', error);
      setError(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    collectMetrics,
    loading,
    error
  };
};
